/**
 * Trail collections — the city-or-park groupings trails are discovered through.
 *
 * WHY A CODE REGISTRY RATHER THAN A TABLE
 * A collection is editorial furniture: a name, a hero, an introduction and the
 * authority that governs the area. There will be a handful of them, they are
 * not client-edited day to day, and they are referenced by slug in URLs. That
 * is the same reasoning already applied to the geography registry, whose IDs
 * are hand-assigned readable strings rather than rows. Trail.collectionSlug is
 * a plain indexed string pointing here, so adding Swat or Nathia Gali later is
 * one entry in this file plus trail rows — no migration, no new route.
 */

export type TrailCollection = {
  slug: string;
  /** "Islamabad Trails" — the customer-facing product name. */
  name: string;
  eyebrow: string;
  /** Used in <title> and the H1. */
  heading: string;
  intro: string;
  city: string;
  province: string;
  protectedArea: string | null;
  heroImage: string;
  heroImageAlt: string;
  /** Who governs access, closures and safety here. Shown, not implied. */
  authorities: Array<{ name: string; role: string; url: string }>;
  metaDescription: string;
};

export const TRAIL_COLLECTIONS: TrailCollection[] = [
  {
    slug: "islamabad",
    name: "Islamabad Trails",
    eyebrow: "GreenPak Trails",
    heading: "Islamabad Trails",
    intro:
      "The Margalla Hills rise straight out of the capital, and a numbered network of trails climbs into them from the edge of the city. This is GreenPak's working guide to those trails: what each one is, where it starts, and what the responsible authorities actually say about it.",
    city: "Islamabad",
    province: "Islamabad Capital Territory",
    protectedArea: "Margalla Hills National Park",
    heroImage: "/images/islamabad/margalla-trail.jpg",
    heroImageAlt: "A footpath climbing through wooded slopes in the Margalla Hills above Islamabad.",
    authorities: [
      {
        name: "Islamabad Wildlife Management Board",
        role: "Manages Margalla Hills National Park and its trail network",
        url: "https://iwmb.gov.pk/",
      },
      {
        name: "Capital Development Authority",
        role: "Publishes hiking information and safety guidance for the trails",
        url: "https://www.cda.gov.pk/public/adventure",
      },
    ],
    metaDescription:
      "A verified guide to the numbered hiking trails in the Margalla Hills above Islamabad — trailheads, difficulty, timings and what the park authorities publish about each one.",
  },
];

export function getTrailCollection(slug: string): TrailCollection | null {
  return TRAIL_COLLECTIONS.find((collection) => collection.slug === slug) ?? null;
}

export function isTrailCollectionSlug(slug: unknown): slug is string {
  return typeof slug === "string" && TRAIL_COLLECTIONS.some((c) => c.slug === slug);
}

/** Canonical URLs. Collection sits in the path so a second city is additive. */
export function trailCollectionHref(collectionSlug: string): string {
  return `/trails/${collectionSlug}`;
}

export function trailHref(collectionSlug: string, trailSlug: string): string {
  return `/trails/${collectionSlug}/${trailSlug}`;
}
